import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import PaystackPop from '@paystack/inline-js'

function PricingPage({ onSelectPlan, currentPlan = 'professional', companyId, session }) {
  const navigate = useNavigate()
  const [billingCycle, setBillingCycle] = useState('monthly')
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [companyCurrency, setCompanyCurrency] = useState('USD')
  const [exchangeRate, setExchangeRate] = useState(1)
  const [actualCurrentPlan, setActualCurrentPlan] = useState(currentPlan)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)
  const [paymentProcessing, setPaymentProcessing] = useState(false)

  const paystackPublicKey = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY || ''
  console.log('🔑 Paystack Public Key:', paystackPublicKey ? '✅ Loaded' : '❌ Missing')

  // Fetch exchange rate for NGN
  useEffect(() => {
    const fetchNgnRate = async () => {
      try {
        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD')
        const data = await response.json()
        if (data.rates && data.rates.NGN) {
          setExchangeRate(data.rates.NGN)
          console.log('💱 Exchange rate: 1 USD =', data.rates.NGN, 'NGN')
        }
      } catch (error) {
        console.error('Exchange rate fetch error:', error)
        setExchangeRate(1550)
      }
    }
    fetchNgnRate()
  }, [])

  useEffect(() => {
    const getCompanyPlan = async () => {
      if (!companyId) return
      
      try {
        const { data: company, error } = await supabase
          .from('companies')
          .select('subscription_plan')
          .eq('id', companyId)
          .maybeSingle()
        
        if (error) {
          console.error('Error fetching company plan:', error)
          return
        }
        
        if (company) {
          setActualCurrentPlan(company.subscription_plan)
        }
      } catch (err) {
        console.error('Failed to fetch company plan:', err)
      } finally {
        setInitialLoadComplete(true)
      }
    }
    
    getCompanyPlan()
  }, [companyId])

  useEffect(() => {
    const getCompanyCurrency = async () => {
      if (!companyId) return
      
      try {
        const { data: company, error } = await supabase
          .from('companies')
          .select('country')
          .eq('id', companyId)
          .maybeSingle()
        
        if (error) {
          console.error('Error fetching company currency:', error)
          return
        }
        
        if (company) {
          const currencyMap = {
            'Nigeria': 'NGN',
            'Ghana': 'GHS',
            'Kenya': 'KES',
            'South Africa': 'ZAR',
            'United States': 'USD',
            'United Kingdom': 'GBP'
          }
          const localCurrency = currencyMap[company.country] || 'USD'
          setCompanyCurrency(localCurrency)
        }
      } catch (err) {
        console.error('Failed to fetch company currency:', err)
        setCompanyCurrency('USD')
      }
    }
    
    getCompanyCurrency()
  }, [companyId])

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price_monthly_usd: 0,
      price_yearly_usd: 0,
      features: [
        '1 Agent',
        '3 Properties',
        '10 Leads',
        '10 Clients',
        'Basic Support'
      ],
      icon: '🆓',
      color: '#6b7280',
      buttonText: 'Downgrade to Free',
      buttonStyle: 'secondary'
    },
    {
      id: 'professional',
      name: 'Professional',
      price_monthly_usd: 49,
      price_yearly_usd: 499,
      features: [
        '5 Agents',
        '50 Properties',
        '500 Leads',
        '500 Clients',
        'Priority Support',
        'WhatsApp Integration',
        'QR Codes',
        'Advanced Analytics',
        'Email Marketing'
      ],
      icon: '⭐',
      color: '#f59e0b',
      recommended: true,
      buttonText: 'Upgrade to Professional',
      buttonStyle: 'primary'
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price_monthly_usd: 199,
      price_yearly_usd: 1990,
      features: [
        'Unlimited Agents',
        'Unlimited Properties',
        'Unlimited Leads',
        'Unlimited Clients',
        '24/7 Priority Support',
        'WhatsApp Integration',
        'QR Codes',
        'Advanced Analytics',
        'API Access',
        'Custom Features',
        'Dedicated Account Manager'
      ],
      icon: '👑',
      color: '#1e3a5f',
      buttonText: 'Upgrade to Enterprise',
      buttonStyle: 'primary'
    }
  ]

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'USD': '$',
      'NGN': '₦',
      'GHS': '₵',
      'KES': 'KSh',
      'ZAR': 'R',
      'GBP': '£',
      'EUR': '€'
    }
    return symbols[currency] || '$'
  }

  const getLocalPrice = (usdAmount) => {
    if (companyCurrency === 'USD') return `$${usdAmount}`
    const converted = Math.round(usdAmount * exchangeRate)
    return `${getCurrencySymbol(companyCurrency)}${converted.toLocaleString()}`
  }

  // 🔥 FIXED: Call Supabase Edge Function instead of Paystack directly
 const initializeTransaction = async (plan, cycle) => {
  const usdAmount = cycle === 'monthly' ? plan.price_monthly_usd : plan.price_yearly_usd
  const ngnAmount = Math.round(usdAmount * exchangeRate)
  const amountInKobo = ngnAmount * 100

  try {
    console.log('📤 Calling Supabase Edge Function...')
    console.log('💰 USD:', usdAmount, 'NGN:', ngnAmount, 'Kobo:', amountInKobo)

    // ✅ CORRECT: Call your Supabase Edge Function
    const response = await fetch('https://jlwhzcghzstmaltfpesx.supabase.co/functions/v1/paystack-init', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
        // ❌ NO Authorization header needed - the function uses the secret key internally
      },
      body: JSON.stringify({
        email: session?.user?.email || 'user@example.com',
        amount: amountInKobo,
        currency: 'NGN',
        metadata: {
          company_id: companyId,
          plan_id: plan.id,
          billing_cycle: cycle,
          user_id: session?.user?.id,
          amount_usd: usdAmount,
          plan_name: plan.name
        },
        callback_url: `${window.location.origin}/leadership`
      })
    })

    const data = await response.json()
    console.log('📥 Edge Function response:', data)

    if (data.success && data.access_code) {
      return data.access_code
    } else {
      console.error('Transaction initialization failed:', data.error)
      alert('Payment initialization failed: ' + (data.error || 'Please try again.'))
      return null
    }
  } catch (error) {
    console.error('Error initializing transaction:', error)
    alert('Error initializing payment. Please try again.')
    return null
  }
}

  const handlePaymentSuccess = async (response) => {
    console.log('✅ Payment successful:', response)
    setPaymentProcessing(false)
    
    try {
      const plan = selectedPlan
      const cycle = billingCycle
      
      const { error: updateError } = await supabase
        .from('companies')
        .update({
          subscription_plan: plan.id,
          subscription_status: 'active',
          payment_status: 'completed',
          billing_cycle: cycle,
          updated_at: new Date()
        })
        .eq('id', companyId)

      if (updateError) {
        console.error('Error updating company:', updateError)
        alert('Payment was successful but we couldn\'t update your account. Please contact support.')
        return
      }

      await supabase
        .from('transactions')
        .insert({
          description: `Subscription payment: ${plan.name} (${cycle})`,
          type: 'income',
          category: 'Subscription',
          amount: cycle === 'monthly' ? plan.price_monthly_usd : plan.price_yearly_usd,
          currency: 'USD',
          transaction_date: new Date().toISOString().split('T')[0],
          company_id: companyId,
          reference: response.reference || response.transaction_ref,
          payment_status: 'completed'
        })

      await supabase
        .from('company_settings')
        .upsert({
          company_id: companyId,
          subscription_plan: plan.id,
          subscription_status: 'active',
          billing_cycle: cycle,
          trial_ends_at: null,
          updated_at: new Date()
        })

      setActualCurrentPlan(plan.id)
      
      if (onSelectPlan) {
        onSelectPlan(plan)
      }

      alert(`✅ Payment successful! You are now on the ${plan.name} plan.`)
      setShowPaymentModal(false)
      setSelectedPlan(null)
      navigate('/leadership')
      
    } catch (err) {
      console.error('Error processing payment:', err)
      alert('Payment successful but there was an error updating your account. Please contact support.')
    }
  }

  const handlePaymentClose = () => {
    console.log('Payment closed')
    setPaymentProcessing(false)
    setShowPaymentModal(false)
    alert('Payment was cancelled. You can try again anytime.')
  }

  // 🔥 FIXED: Use PaystackPop with access_code
  const handlePayNow = async () => {
    if (!selectedPlan) {
      alert('Please select a plan first.')
      return
    }
    
    setPaymentProcessing(true)
    
    try {
      // Initialize transaction and get access_code
      const accessCode = await initializeTransaction(selectedPlan, billingCycle)
      
      if (!accessCode) {
        setPaymentProcessing(false)
        return
      }
      
      console.log('✅ Access Code received:', accessCode)
      
      // Initialize PaystackPop
      const paystack = new PaystackPop()
      paystack.resumeTransaction(accessCode, {
        onSuccess: (response) => {
          console.log('✅ Payment success:', response)
          handlePaymentSuccess(response)
        },
        onCancel: () => {
          console.log('❌ Payment cancelled')
          handlePaymentClose()
        }
      })
    } catch (error) {
      console.error('Paystack error:', error)
      setPaymentProcessing(false)
      alert('Payment system error. Please try again.')
    }
  }

  const handlePlanSelect = (plan) => {
    if (plan.id === actualCurrentPlan) {
      alert(`You are already on the ${plan.name} plan.`)
      return
    }
    
    if (plan.id === 'free') {
      if (confirm(`Downgrade to Free plan? You will lose access to premium features.`)) {
        onSelectPlan(plan)
      }
    } else {
      setSelectedPlan(plan)
      setShowPaymentModal(true)
    }
  }

  const goBackToApp = () => {
    if (session?.user?.role === 'leadership') {
      navigate('/leadership')
    } else if (session?.user?.role === 'agent') {
      navigate('/agent')
    } else if (session?.user?.role === 'cro') {
      navigate('/cro')
    } else {
      navigate('/')
    }
  }

  if (!companyId || !session) {
    return (
      <div style={containerStyle}>
        <div style={errorContainerStyle}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔒</div>
          <h2>Unable to Load Pricing</h2>
          <p>Please make sure you're logged in and your account is properly set up.</p>
          <button onClick={() => navigate('/login')} style={errorButtonStyle}>
            Go to Login
          </button>
          <button onClick={() => navigate('/')} style={errorButtonSecondaryStyle}>
            Return to Home
          </button>
        </div>
      </div>
    )
  }

  if (!initialLoadComplete) {
    return (
      <div style={containerStyle}>
        <div style={loadingContainerStyle}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>💰</div>
          <h2>Loading Pricing Plans...</h2>
          <p>Please wait while we fetch the latest pricing.</p>
        </div>
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <button onClick={goBackToApp} style={backButtonStyle}>
          ← Back to Dashboard
        </button>
        <div>
          <h1 style={{ margin: 0 }}>Subscription Plans</h1>
          <p style={{ color: '#64748b' }}>Choose the plan that fits your business</p>
        </div>
        <div style={{ width: '100px' }} />
      </div>

      <div style={billingToggleContainer}>
        <button
          onClick={() => setBillingCycle('monthly')}
          style={{
            ...toggleButton,
            background: billingCycle === 'monthly' ? '#1e3a5f' : '#f8fafc',
            color: billingCycle === 'monthly' ? '#fff' : '#1e293b'
          }}
        >
          Monthly Billing
        </button>
        <button
          onClick={() => setBillingCycle('yearly')}
          style={{
            ...toggleButton,
            background: billingCycle === 'yearly' ? '#1e3a5f' : '#f8fafc',
            color: billingCycle === 'yearly' ? '#fff' : '#1e293b'
          }}
        >
          Yearly Billing <span style={{ fontSize: '12px', opacity: 0.8 }}>(Save 15%)</span>
        </button>
      </div>

      {companyCurrency !== 'USD' && (
        <div style={currencyNoteStyle}>
          💱 Prices shown in {getCurrencySymbol(companyCurrency)} (exchange rate: 1 USD ≈ {exchangeRate} {companyCurrency})
        </div>
      )}

      <div style={pricingGrid}>
        {plans.map(plan => {
          const isCurrentPlan = plan.id === actualCurrentPlan
          const price = billingCycle === 'monthly' ? plan.price_monthly_usd : plan.price_yearly_usd
          const localPrice = getLocalPrice(price)
          
          return (
            <div
              key={plan.id}
              style={{
                ...pricingCard,
                border: isCurrentPlan ? `2px solid ${plan.color}` : '1px solid #e2e8f0',
                transform: isCurrentPlan ? 'scale(1.02)' : 'scale(1)',
                opacity: isCurrentPlan ? 1 : 0.95
              }}
            >
              {plan.recommended && !isCurrentPlan && (
                <div style={recommendedBadge}>Most Popular</div>
              )}
              {isCurrentPlan && (
                <div style={{ ...recommendedBadge, background: '#059669' }}>Current Plan</div>
              )}
              
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>{plan.icon}</div>
              <h2 style={{ color: plan.color, marginBottom: '8px' }}>{plan.name}</h2>
              
              <div style={{ margin: '16px 0' }}>
                <span style={{ fontSize: '36px', fontWeight: 'bold' }}>
                  {localPrice}
                </span>
                <span style={{ color: '#64748b' }}>/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                {companyCurrency !== 'USD' && price > 0 && (
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    ≈ ${price} USD
                  </div>
                )}
              </div>

              <ul style={featuresList}>
                {plan.features.map((feature, idx) => (
                  <li key={idx} style={featureItem}>
                    <span style={{ color: '#059669', marginRight: '8px' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handlePlanSelect(plan)}
                disabled={isCurrentPlan}
                style={{
                  ...pricingButton,
                  background: isCurrentPlan ? '#e2e8f0' : plan.color,
                  color: isCurrentPlan ? '#64748b' : '#fff',
                  cursor: isCurrentPlan ? 'default' : 'pointer',
                  opacity: isCurrentPlan ? 0.7 : 1
                }}
              >
                {isCurrentPlan ? 'Current Plan' : plan.buttonText}
              </button>
            </div>
          )
        })}
      </div>

      {showPaymentModal && selectedPlan && (
        <div style={modalOverlay} onClick={() => setShowPaymentModal(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <div style={modalHeader}>
              <h2>Complete Your Subscription</h2>
              <button onClick={() => setShowPaymentModal(false)} style={closeBtn}>✕</button>
            </div>

            <div style={paymentSummary}>
              <h3>{selectedPlan.name} Plan</h3>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: selectedPlan.color }}>
                {getLocalPrice(billingCycle === 'monthly' ? selectedPlan.price_monthly_usd : selectedPlan.price_yearly_usd)}
                <span style={{ fontSize: '14px', fontWeight: 'normal' }}>
                  /{billingCycle === 'monthly' ? 'month' : 'year'}
                </span>
              </p>
              <p style={{ color: '#64748b' }}>
                {billingCycle === 'monthly' ? 'Monthly billing' : 'Yearly billing (2 months free)'}
              </p>
            </div>

            <div style={paymentDetails}>
              <h4>Payment Methods Accepted:</h4>
              <div style={paymentIcons}>
                <span>💳 Card</span>
                <span>🏦 Bank Transfer</span>
                <span>📱 USSD</span>
                <span>📲 QR Code</span>
                <span>💰 Mobile Money</span>
              </div>
            </div>

            <button 
              onClick={handlePayNow} 
              disabled={paymentProcessing || loading}
              style={{
                ...payButton,
                opacity: paymentProcessing ? 0.6 : 1,
                cursor: paymentProcessing ? 'not-allowed' : 'pointer'
              }}
            >
              {paymentProcessing ? 'Processing...' : `Pay ${getLocalPrice(billingCycle === 'monthly' ? selectedPlan.price_monthly_usd : selectedPlan.price_yearly_usd)} Now`}
            </button>

            <p style={secureNote}>
              🔒 Secure payment powered by Paystack
            </p>

            <button 
              onClick={() => {
                setShowPaymentModal(false)
                setSelectedPlan(null)
              }} 
              style={cancelModalBtn}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Styles...
const containerStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
  padding: '40px 20px',
  fontFamily: "'Inter', sans-serif"
}

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '40px',
  maxWidth: '1200px',
  margin: '0 auto 40px auto',
  padding: '0 20px'
}

const backButtonStyle = {
  background: '#f1f5f9',
  border: 'none',
  padding: '10px 20px',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '14px',
  color: '#1e293b',
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
}

const billingToggleContainer = {
  display: 'flex',
  justifyContent: 'center',
  gap: '16px',
  marginBottom: '32px'
}

const toggleButton = {
  padding: '12px 32px',
  borderRadius: '40px',
  border: 'none',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  transition: 'all 0.2s'
}

const currencyNoteStyle = {
  textAlign: 'center',
  marginBottom: '24px',
  padding: '8px 16px',
  background: '#fef3c7',
  borderRadius: '8px',
  maxWidth: '500px',
  margin: '0 auto 24px auto',
  fontSize: '13px',
  color: '#92400e'
}

const pricingGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '30px',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 20px'
}

const pricingCard = {
  background: '#fff',
  borderRadius: '20px',
  padding: '32px',
  position: 'relative',
  transition: 'transform 0.2s',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
}

const recommendedBadge = {
  position: 'absolute',
  top: '-12px',
  left: '50%',
  transform: 'translateX(-50%)',
  background: '#f59e0b',
  color: '#fff',
  padding: '4px 16px',
  borderRadius: '20px',
  fontSize: '12px',
  fontWeight: 'bold'
}

const featuresList = {
  listStyle: 'none',
  padding: 0,
  margin: '24px 0',
  minHeight: '250px'
}

const featureItem = {
  padding: '8px 0',
  display: 'flex',
  alignItems: 'center',
  fontSize: '14px'
}

const pricingButton = {
  width: '100%',
  padding: '14px',
  borderRadius: '12px',
  border: 'none',
  fontSize: '16px',
  fontWeight: '600',
  transition: 'all 0.2s'
}

const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  padding: '20px'
}

const modalStyle = {
  background: '#fff',
  borderRadius: '20px',
  width: '100%',
  maxWidth: '450px',
  overflow: 'hidden'
}

const modalHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px',
  borderBottom: '1px solid #e2e8f0'
}

const closeBtn = {
  background: 'none',
  border: 'none',
  fontSize: '20px',
  cursor: 'pointer',
  color: '#64748b'
}

const paymentSummary = {
  padding: '24px',
  textAlign: 'center',
  background: '#f8fafc'
}

const paymentDetails = {
  padding: '20px 24px',
  borderBottom: '1px solid #e2e8f0'
}

const paymentIcons = {
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
  marginTop: '12px',
  fontSize: '13px'
}

const payButton = {
  width: 'calc(100% - 48px)',
  margin: '24px',
  padding: '14px',
  background: '#059669',
  color: '#fff',
  border: 'none',
  borderRadius: '12px',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer'
}

const secureNote = {
  textAlign: 'center',
  fontSize: '12px',
  color: '#64748b',
  margin: '0 24px 24px 24px'
}

const cancelModalBtn = {
  width: 'calc(100% - 48px)',
  margin: '0 24px 24px 24px',
  padding: '12px',
  background: '#f1f5f9',
  color: '#1e293b',
  border: 'none',
  borderRadius: '12px',
  cursor: 'pointer'
}

const errorContainerStyle = {
  textAlign: 'center',
  padding: '60px 20px',
  background: '#fff',
  borderRadius: '20px',
  maxWidth: '500px',
  margin: '100px auto',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
}

const errorButtonStyle = {
  background: '#1e3a5f',
  color: '#fff',
  border: 'none',
  padding: '12px 24px',
  borderRadius: '8px',
  fontSize: '16px',
  cursor: 'pointer',
  marginTop: '20px',
  marginRight: '10px'
}

const errorButtonSecondaryStyle = {
  background: '#f1f5f9',
  color: '#1e293b',
  border: 'none',
  padding: '12px 24px',
  borderRadius: '8px',
  fontSize: '16px',
  cursor: 'pointer',
  marginTop: '20px'
}

const loadingContainerStyle = {
  textAlign: 'center',
  padding: '60px 20px',
  background: '#fff',
  borderRadius: '20px',
  maxWidth: '500px',
  margin: '100px auto',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
}

export default PricingPage